import { Link, useNavigate } from 'react-router-dom';

export default function Footer() {
  const navigate = useNavigate();

  const scrollToSection = (sectionId: string) => {
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
      setTimeout(() => {
        const target = document.getElementById(sectionId);
        if (target) target.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  };

  type DirectoryLink =
    | { label: string; sectionId: string; href?: never }
    | { label: string; href: string; sectionId?: never };

  const directoryLinks: DirectoryLink[] = [
    { label: 'Home', href: '/' },
    { label: 'All Categories', href: '/categories' },
    { label: 'AI Tools', sectionId: 'tools-directory' },
    { label: 'Featured Tools', sectionId: 'featured-tools' },
    { label: 'New Tools', sectionId: 'new-tools' },
    { label: 'AI Automation Tools', href: '/category/ai-automation-tools' },
  ];

  return (
    <footer className="bg-gray-900 border-t border-teal-500/20 relative">
      <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-teal-500/50 to-transparent"></div>
      <div className="absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-teal-500/5 to-transparent"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">

          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6 group cursor-pointer">
              <img
                src="https://static.readdy.ai/image/81ddcca4e455aa4e3e9404d7fe6eefde/e7fd262c64409f252189cfe0fbf7a092.png"
                alt="Toolnova"
                className="h-32 w-auto object-contain transition-all duration-300 opacity-90 group-hover:opacity-100 group-hover:drop-shadow-[0_0_12px_rgba(45,212,191,0.4)]"
              />
            </Link>
            <p className="text-sm text-gray-300 leading-relaxed mb-7">
              Discover the best AI tools for work, creativity, and business through curated recommendations.
            </p>
            <div className="flex gap-3">
              {[
                { icon: 'ri-twitter-x-line', href: 'https://x.com/toolnovaHQ', label: 'X (Twitter)' },
                { icon: 'ri-youtube-line', href: 'https://youtube.com/@toolnovaHQ', label: 'YouTube' },
                { icon: 'ri-instagram-line', href: 'https://instagram.com/toolnova.ai', label: 'Instagram' },
                { icon: 'ri-tiktok-line', href: 'https://tiktok.com/@toolnova.ai', label: 'TikTok' },
              ].map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  title={social.label}
                  className="w-10 h-10 flex items-center justify-center bg-gray-800/60 hover:bg-teal-500/20 border border-gray-700/60 hover:border-teal-500/60 rounded-lg text-gray-300 hover:text-teal-400 transition-all duration-300 cursor-pointer hover:scale-110 hover:shadow-lg hover:shadow-teal-500/20"
                >
                  <i className={`${social.icon} text-base`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Directory Links */}
          <div>
            <h4 className="text-xs font-bold text-gray-200 uppercase tracking-widest mb-6">Directory</h4>
            <ul className="space-y-3.5">
              {directoryLinks.map((link) => (
                <li key={link.label}>
                  {link.href ? (
                    <Link
                      to={link.href}
                      className="group inline-flex items-center gap-1.5 text-sm text-gray-300 hover:text-teal-400 transition-colors duration-200 cursor-pointer"
                    >
                      <i className="ri-arrow-right-s-line text-xs opacity-0 group-hover:opacity-100 -ml-4 group-hover:ml-0 transition-all duration-200"></i>
                      {link.label}
                    </Link>
                  ) : (
                    <button
                      onClick={() => scrollToSection(link.sectionId)}
                      className="group inline-flex items-center gap-1.5 text-sm text-gray-300 hover:text-teal-400 transition-colors duration-200 cursor-pointer bg-transparent border-none p-0"
                    >
                      <i className="ri-arrow-right-s-line text-xs opacity-0 group-hover:opacity-100 -ml-4 group-hover:ml-0 transition-all duration-200"></i>
                      {link.label}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-xs font-bold text-gray-200 uppercase tracking-widest mb-6">Company</h4>
            <ul className="space-y-3.5">
              {[
                { label: 'About', href: '/about' },
                { label: 'Blog', href: '/blog' },
                { label: 'Submit Tool', href: '/submit-tool' },
                { label: 'Contact', href: '/contact' },
              ].map((link) => (
                <li key={link.label}>
                  <Link to={link.href} className="group inline-flex items-center gap-1.5 text-sm text-gray-300 hover:text-teal-400 transition-colors duration-200 cursor-pointer">
                    <i className="ri-arrow-right-s-line text-xs opacity-0 group-hover:opacity-100 -ml-4 group-hover:ml-0 transition-all duration-200"></i>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="text-xs font-bold text-gray-200 uppercase tracking-widest mb-6">Legal</h4>
            <ul className="space-y-3.5">
              {[
                { label: 'Privacy Policy', href: '/privacy-policy' },
                { label: 'Terms of Service', href: '/terms-of-service' },
                { label: 'Affiliate Disclosure', href: '/affiliates' },
                { label: 'Cookie Policy', href: '/cookie-policy' },
              ].map((link) => (
                <li key={link.label}>
                  <Link to={link.href} className="group inline-flex items-center gap-1.5 text-sm text-gray-300 hover:text-teal-400 transition-colors duration-200 cursor-pointer">
                    <i className="ri-arrow-right-s-line text-xs opacity-0 group-hover:opacity-100 -ml-4 group-hover:ml-0 transition-all duration-200"></i>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-gray-800/60 flex flex-col md:flex-row justify-between items-center gap-4 text-center md:text-left">
          <div className="flex flex-col md:flex-row items-center gap-2 md:gap-4">
            <p className="text-xs text-gray-300">
              © 2026 ToolNova. All rights reserved.
            </p>
            <p className="text-xs text-gray-500">
              ToolNova participates in affiliate programs. We may earn a commission at no extra cost to you.
            </p>
          </div>
          <p className="text-xs text-gray-300">
            Built for discovering the best AI tools.
          </p>
        </div>
      </div>
    </footer>
  );
}
